import { useEffect } from 'react';

import type { ImageProps } from 'components/common/Image';
import { loadImage } from 'components/common/Image';

interface UsePreloadAdjacentImagesOptions {
  images: ImageProps[];
  currentIndex: number;
  enabled: boolean;
}

/**
 * Warms the images on either side of `currentIndex` (wrapping) into cache so the next/prev
 * swipe paints with no blank frame. Inert unless `enabled` (touch-only swipe card, after first
 * swipe), keeping page load free of extra image requests. Preload failures are swallowed.
 */
export const usePreloadAdjacentImages = ({ images, currentIndex, enabled }: UsePreloadAdjacentImagesOptions): void => {
  useEffect(() => {
    if (!enabled || images.length <= 1) {
      return;
    }
    const total = images.length;
    const next = (currentIndex + 1) % total;
    const prev = (currentIndex - 1 + total) % total;
    const nextImage = images[next];
    const prevImage = images[prev];
    if (nextImage) {
      loadImage(nextImage).catch(() => {});
    }
    // Skip the duplicate request when prev and next resolve to the same image (2-image set).
    if (prevImage && prev !== next) {
      loadImage(prevImage).catch(() => {});
    }
  }, [images, currentIndex, enabled]);
};
